import { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { useTheme } from '@mui/material';
import { MagnifyingGassIcon, SearchCloseIcon } from 'app/components/Icons';
import { ButtonPrimary } from 'app/components/ButtonPrimary';
import { TextM } from 'app/components/typography';
import { serializeOnlyNumbers } from 'app/utils/normalizer';
import { isValidCPF } from 'app/utils/validations';
import {
  ButtonArea,
  StyledButtonIcon,
  StyledContainer,
  StyledIcon,
  StyledSearchInput,
  StyledSearchWrapper,
  StyledSpan,
} from 'app/components/SearchFilter/SearchInput/SearchInput.styles';
import {
  StyledFieldsRow,
  StyledFieldWrapper,
} from './MarginSearchForm.styles';

type TMarginSearchFormProps = {
  textButton?: string;
  searchHandler: (taxId: string, benefitNumber?: string) => void;
  showBenefitNumber?: boolean;
};

const MarginSearchForm = ({
  textButton = 'Consultar margem',
  searchHandler,
  showBenefitNumber = false,
}: TMarginSearchFormProps) => {
  const theme = useTheme();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [cpfValue, setCpfValue] = useState('');
  const [benefitNumber, setBenefitNumber] = useState('');

  const serializedCpf = serializeOnlyNumbers(cpfValue);
  const serializedBenefit = serializeOnlyNumbers(benefitNumber);
  const isCpfValid = serializedCpf.length === 11 && isValidCPF(serializedCpf);
  const isBenefitFilled = serializedBenefit.length > 0;
  const isButtonDisabled = showBenefitNumber
    ? !isCpfValid || !!errorMessage || !isBenefitFilled
    : !isCpfValid || !!errorMessage;

  useEffect(() => {
    if (serializedCpf.length === 11 && !isValidCPF(serializedCpf)) {
      setErrorMessage('CPF inválido');
    }
  }, [serializedCpf]);

  const handleSearch = () => {
    if (isButtonDisabled) return;
    searchHandler(
      serializedCpf,
      showBenefitNumber ? serializedBenefit : undefined,
    );
  };

  return (
    <StyledSearchWrapper>
      <StyledFieldsRow>
        <StyledFieldWrapper>
          {showBenefitNumber && (
            <TextM weight="Bold" style={{ marginBottom: '10px' }}>
              CPF
            </TextM>
          )}
          <StyledContainer>
            <StyledSearchInput>
              <StyledIcon>
                <MagnifyingGassIcon
                  color={theme.palette.brand.gray[500] as string}
                />
              </StyledIcon>
              <InputMask
                mask="999.999.999-99"
                placeholder={
                  showBenefitNumber
                    ? 'Digite o CPF'
                    : 'Digite o CPF para consultar a margem'
                }
                value={cpfValue}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                onChange={(e) => {
                  setCpfValue(e.target.value);
                  setErrorMessage(undefined);
                }}
              >
                {(inputProps) => <input {...inputProps} />}
              </InputMask>
              {cpfValue && (
                <ButtonArea onClick={() => setCpfValue('')}>
                  <SearchCloseIcon
                    color={theme.palette.brand.primary.base as string}
                    size={14}
                  />
                </ButtonArea>
              )}
            </StyledSearchInput>
            <StyledSpan style={{ color: 'red' }}>{errorMessage}</StyledSpan>
          </StyledContainer>
        </StyledFieldWrapper>

        {showBenefitNumber && (
          <StyledFieldWrapper>
            <TextM weight="Bold" style={{ marginBottom: '10px' }}>
              Número do benefício
            </TextM>
            <StyledContainer>
              <StyledSearchInput>
                <input
                  placeholder="Digite o número do benefício"
                  value={benefitNumber}
                  inputMode="numeric"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  onChange={(e) =>
                    setBenefitNumber(serializeOnlyNumbers(e.target.value))
                  }
                />
                {benefitNumber && (
                  <ButtonArea onClick={() => setBenefitNumber('')}>
                    <SearchCloseIcon
                      color={theme.palette.brand.primary.base as string}
                      size={14}
                    />
                  </ButtonArea>
                )}
              </StyledSearchInput>
              <StyledSpan />
            </StyledContainer>
          </StyledFieldWrapper>
        )}
      </StyledFieldsRow>

      <ButtonPrimary
        onClick={handleSearch}
        style={{
          width: '17rem',
          marginLeft: '2rem',
          marginTop: showBenefitNumber ? '2.1rem' : 0,
          alignSelf: 'flex-start',
        }}
        disabled={isButtonDisabled}
      >
        <div>{textButton}</div>
        <StyledButtonIcon>
          <MagnifyingGassIcon
            color={theme.palette.brand.gray[100] as string}
          />
        </StyledButtonIcon>
      </ButtonPrimary>
    </StyledSearchWrapper>
  );
};

export default MarginSearchForm;
